import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { analyticsApi } from '@/api';
import {
  Badge,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  LinkButton,
  LoadingState,
  MetricCard,
  PageHeader,
} from '@/components/ui';
import { HiringFunnel } from '@/features/analytics/HiringFunnel';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { TrendChart } from '@/features/analytics/TrendChart';
import { LISTING_SERIES } from '@/features/analytics/series';
import { count, days, percent } from '@/features/analytics/format';
import { INTERNSHIP_STATUS_LABELS, INTERNSHIP_STATUS_TONES } from '@/features/internships/status';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate } from '@/utils/format';

/**
 * How one listing is doing: who looked, who applied, how far they got.
 *
 * Read with `internship.read`, so it is reachable from the internship list by
 * anyone who manages listings -- the company-wide Analytics page stays with the
 * people who hold `analytics.read`.
 */
export function CompanyInternshipPerformancePage() {
  const { id = '' } = useParams();
  const [period, setPeriod] = useState(30);

  const {
    data: report,
    isPending,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['company-internship-performance', id, period],
    queryFn: () => analyticsApi.getListingPerformance(id, period),
  });

  const back = (
    <LinkButton to={paths.company.internshipDetail(id)} variant="secondary">
      <Icon name="internship" size={16} />
      Back to the listing
    </LinkButton>
  );

  if (isPending) return <LoadingState label="Crunching this listing's numbers…" />;
  if (isError) {
    return (
      <div>
        <PageHeader title="Performance" action={back} />
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </div>
    );
  }

  const { listing, traffic, speed, exits, fill } = report;
  // The server clamps the window to the plan, so label with what it used.
  const window = report.period.days;

  return (
    <div>
      <PageHeader
        eyebrow="Performance"
        title={listing.title}
        description={
          <span className="flex flex-wrap items-center gap-2">
            <Badge dot tone={INTERNSHIP_STATUS_TONES[listing.status] ?? 'neutral'}>
              {INTERNSHIP_STATUS_LABELS[listing.status] ?? listing.status}
            </Badge>
            <span>
              {listing.published_at
                ? `Live since ${formatDate(listing.published_at)}`
                : 'Never published'}
              {listing.application_deadline &&
                ` · Deadline ${formatDate(listing.application_deadline)}`}
            </span>
          </span>
        }
        action={back}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-ink-muted text-sm">
          {window === period
            ? `The last ${window} days`
            : `The last ${window} days — the most your plan covers`}
        </p>
        <PeriodPicker value={period} onChange={setPeriod} />
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Visitors"
          icon="eye"
          value={count(traffic.visitors)}
          hint={`${count(traffic.views)} ${traffic.views === 1 ? 'view' : 'views'} in total`}
        />
        <MetricCard
          label="Applications"
          icon="application"
          tone="info"
          value={count(report.applications)}
          hint={`${percent(traffic.visitor_to_application_rate)} of visitors applied`}
        />
        <MetricCard
          label="Saved"
          icon="bookmark"
          tone="warning"
          value={count(traffic.saves)}
          hint="Students who bookmarked it"
        />
        <MetricCard
          label="Positions filled"
          icon="check"
          tone="success"
          value={`${count(fill.hired)} / ${count(fill.positions)}`}
          hint="Since the listing went live"
        />
      </div>

      <div className="mb-6 grid items-start gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Card>
          <CardHeader
            title="Hiring funnel"
            description="Applications received in this period, and how far each one got."
          />
          <HiringFunnel stages={report.funnel} />
        </Card>

        <Card>
          <CardHeader title="Reach" description="Who found the listing." />
          <dl className="space-y-3 text-sm">
            <Metric
              label="Views"
              value={count(traffic.views)}
              hint="Every time the listing was opened"
            />
            <Metric
              label="Visitors"
              value={count(traffic.visitors)}
              hint="Each person once a day, so reloads do not count twice"
            />
            <Metric
              label="Signed-in students"
              value={count(traffic.students)}
              hint="Distinct students who opened it"
            />
            <Metric
              label="All-time views"
              value={count(traffic.lifetime_views)}
              hint="Since publishing, including before dated tracking began"
            />
          </dl>
        </Card>
      </div>

      <Card className="mb-6">
        <CardHeader
          title="Over time"
          description="Visitors, applications and hires as they happened."
        />
        <TrendChart
          points={report.trend}
          series={LISTING_SERIES}
          granularity={report.period.granularity}
          emptyLabel="No activity in this period"
        />
      </Card>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Responsiveness" description="How long applicants wait on you." />
          <dl className="space-y-3 text-sm">
            <Metric
              label="First application"
              value={days(fill.days_to_first_application)}
              hint="After the listing went live"
            />
            <Metric
              label="First response"
              value={days(speed.median_days_to_first_response)}
              hint="Median wait for any answer at all"
            />
            <Metric
              label="Applications answered"
              value={percent(speed.response_rate)}
              hint="The rest are still sitting in the inbox"
            />
            <Metric
              label="Time to hire"
              value={days(speed.median_days_to_hire)}
              hint="Application to hire"
            />
          </dl>
        </Card>

        <Card>
          <CardHeader title="Where they went" description="Everyone who left the pipeline." />
          <dl className="space-y-3 text-sm">
            <Metric label="Still in progress" value={count(exits.in_progress)} />
            <Metric label="Rejected" value={count(exits.rejected)} />
            <Metric
              label="Withdrew"
              value={count(exits.withdrawn)}
              hint="Candidates who stepped out themselves"
            />
          </dl>
        </Card>
      </div>
    </div>
  );
}

function Metric({ label, value, hint }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="min-w-0">
        <span className="text-ink">{label}</span>
        {hint && <span className="text-ink-muted block text-xs">{hint}</span>}
      </dt>
      <dd className="text-ink shrink-0 font-medium">{value}</dd>
    </div>
  );
}
